---
title: "Activating your licence"
slug: "activating-your-licence"
category: "getting-started"
description: "Turn your 30-day trial into a permanent licence with a key from the portal."
tags: ["licence", "activate", "key", "purchase", "trial"]
order: 2
---

Once you've decided BookBurrow is right for your school and you've bought a licence, activating it is a two-minute job. Your trial data carries over exactly as it is — nothing is reset or reimported.

## Before you start

You'll need:

- Your **licence key**, which we email to the address you used at checkout. It looks like `BB-XXXX-XXXX-XXXX-XXXX`.
- The computer you want to use BookBurrow on long-term. A licence ties to one machine at a time; see [Transferring to a new computer](/help/guides/transferring-to-a-new-computer) if that changes later.
- An internet connection, just for the activation step itself. BookBurrow only needs to contact our server long enough to validate the key.

<Callout variant="info" title="No licence key yet?">
You can buy one at any point during or after your trial. Sign in at [bookburrow.uk/portal](/portal/login), go to **Settings → Upgrade**, and follow the checkout. Licence keys arrive by email within a few minutes for card payments, or once payment clears for invoiced BACS orders.
</Callout>

## Activating the licence

<Steps>
  <Step title="Open BookBurrow">
    If you're not already signed in, enter your admin PIN to unlock the Settings area.
  </Step>
  <Step title="Go to Settings → Licence">
    You'll see your current trial status and a countdown to when the trial ends.
  </Step>
  <Step title="Paste your licence key">
    Copy the key exactly as it appears in the email (including the dashes) and paste it into the **Activate licence** box. Click **Activate**.
  </Step>
  <Step title="Wait a couple of seconds">
    BookBurrow contacts our server, validates the key, and records this computer as one of your activations. You'll see a green "Licence active" banner and the trial countdown disappears.
  </Step>
</Steps>

That's it — your library is now fully licensed, with no further action needed.

## What activation records

When you activate, BookBurrow sends us:

- Your **licence key**.
- A **machine fingerprint** — a hashed identifier made from your computer's hardware details. We use this to make sure a single-seat licence is only active on one computer at a time. The fingerprint is one-way: we can't use it to identify your computer on a network or to track it in any other way.

That's everything. No pupil data, no library contents, no personal information about you.

## If activation fails

Activation can fail for a small number of reasons. In order of how often we see them:

1. **"Invalid licence key"** — double-check you've copied the key exactly, including dashes, and that you haven't added a trailing space. Licence keys are case-insensitive but everything else matters.
2. **"This licence is already active on another computer"** — you've hit the single-seat limit. Either deactivate the other machine from its copy of BookBurrow (Settings → Licence → Deactivate), or email us and we'll help.
3. **"Cannot reach activation server"** — BookBurrow couldn't open an HTTPS connection to `api.bookburrow.uk`. This usually means the school firewall is blocking it. Forward [the list of network endpoints from the install guide](/help/guides/installing-bookburrow) to your IT contact.
4. **"Licence has been revoked"** — this is rare and almost always points to a billing issue (for example, a chargeback). Email support@bookburrow.uk with your licence key and we'll look into it.

<Callout variant="success" title="Once activated, you're done forever">
You only have to activate a licence once per computer. BookBurrow remembers the activation and works completely offline from then on — including future restarts, updates, and unplugging from the internet entirely.
</Callout>
